import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';
import { CONSTANTS } from '../../app/shared/constants/constants';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly storageKey = 'advokit.language';
  private readonly defaultLanguage = 'bg';
  private supportedLanguages = ['bg'];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private translate: TranslateService,
    private primeNG: PrimeNG
  ) {}

  init(): Promise<void> {
    const storedLanguage = this.getStoredLanguage();
    const language = this.isSupported(storedLanguage) ? storedLanguage : this.defaultLanguage;

    this.translate.addLangs(this.supportedLanguages);
    this.translate.setFallbackLang(this.defaultLanguage);
    this.translate.use(language);

    return firstValueFrom(this.translate.get(CONSTANTS.CALENDAR)).then(() => {
      this.applyPrimeNGCalendarTranslations();
    });
  }

  private applyPrimeNGCalendarTranslations(): void {
    const cal = this.translate.instant(CONSTANTS.CALENDAR) as {
      DAY_NAMES?: string[];
      DAY_NAMES_SHORT?: string[];
      DAY_NAMES_MIN?: string[];
      MONTH_NAMES?: string[];
      MONTH_NAMES_SHORT?: string[];
      FIRST_DAY_OF_WEEK?: number;
      TODAY?: string;
      CLEAR?: string;
      WEEK_HEADER?: string;
    };

    const current = this.primeNG.translation || {};
    this.primeNG.setTranslation({
      ...current,
      dayNames: cal.DAY_NAMES ?? current.dayNames,
      dayNamesShort: cal.DAY_NAMES_SHORT ?? current.dayNamesShort,
      dayNamesMin: cal.DAY_NAMES_MIN ?? current.dayNamesMin,
      monthNames: cal.MONTH_NAMES ?? current.monthNames,
      monthNamesShort: cal.MONTH_NAMES_SHORT ?? current.monthNamesShort,
      firstDayOfWeek: cal.FIRST_DAY_OF_WEEK ?? current.firstDayOfWeek ?? 1,
      today: cal.TODAY ?? current.today,
      clear: cal.CLEAR ?? current.clear,
      weekHeader: cal.WEEK_HEADER ?? current.weekHeader
    });
  }

  get currentLanguage(): string {
    return this.translate.getCurrentLang() || this.translate.getFallbackLang() || this.defaultLanguage;
  }

  setLanguage(language: string): void {
    if (!this.isSupported(language)) {
      return;
    }

    this.translate.use(language)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyPrimeNGCalendarTranslations();
      });
    this.persistLanguage(language);
  }

  registerLanguage(language: string): void {
    if (this.supportedLanguages.includes(language)) {
      return;
    }

    this.supportedLanguages.push(language);
    this.translate.addLangs([language]);
  }

  private getStoredLanguage(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  private persistLanguage(language: string): void {
    try {
      localStorage.setItem(this.storageKey, language);
    } catch {
      // Ignore storage errors (private mode, disabled storage, etc.)
    }
  }

  private isSupported(language: string | null | undefined): language is string {
    return !!language && this.supportedLanguages.includes(language);
  }
}
